import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import { APIError, AssetAllocationItem, FormFieldConfig } from "../helpers/shared-interfaces";
import { generateDisplayErrorMessage, handleValidationErrors, mapAPIErrorToFormError, request, responseHandler , showTotalPagination } from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { Button, Col, FormInstance, Row, Table, message } from "antd";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { PlusOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import { CREATE_ASSET_ALLOCATION_ITEM, GET_ASSET_ALLOCATION_ITEMS } from "../gql/economicIndicators";
import ModalCreator from "../components/ModalCreator";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import { PageToolbar } from "../components/PageToolbar";

interface State {
  isLoading: boolean;
  assetAllocationItems: AssetAllocationItem[];
  page: number;
  pageSize: number;
  name: string;
  id?: number;
  showNewAssetAllocationItemModal: boolean;
  isCreatingAssetAllocationItem: boolean;
}

export function getAssetAllocationItemsColumn<T extends AssetAllocationItem>(
  assetAllocationItems: AssetAllocationItem[],
): ColumnType<T>[] {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
  ];
}

export const NewAssetAllocationItemUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "name",
      name: "name",
      rule: "string",
      inputType: "string",
      required: true,
      label: "Name",
      placeholder: "Enter Asset Allocation Item Name",
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <BaseForm
      layout="vertical"
      reference={props.reference}
      fieldsToHide={[]}
      fields={fields}
    />
  );
};

export default class AssetAllocationItems extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      assetAllocationItems: [],
      page: 1,
      pageSize: 25,
      name: "",
      showNewAssetAllocationItemModal: false,
      isCreatingAssetAllocationItem: false
    };
    this.reference = React.createRef<FormInstance>();
    this.nextPage = this.nextPage.bind(this);
    this.toggleCreateAssetAllocationItemModal =
      this.toggleCreateAssetAllocationItemModal.bind(this);
    this.createAssetAllocationItem = this.createAssetAllocationItem.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.getAssetAllocationItems();
  }

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      {
        isLoading: true,
        page,
        pageSize,
      },
      async () => {
        await this.getAssetAllocationItems();
      },
    );
  }

  toggleCreateAssetAllocationItemModal(): void {
    this.setState((prevState) => ({
      showNewAssetAllocationItemModal: !prevState.showNewAssetAllocationItemModal
    }));
  }

  getAssetAllocationItems = async (): Promise<void> => {
    const { page } = this.state;
    this.setState({ isLoading: true });
    try {
      const response: any = await request({
        query: GET_ASSET_ALLOCATION_ITEMS,
        variables: { page },
      });
      const { data } = response;
      this.setState({
        assetAllocationItems: data.data?.assetAllocationItems || [],
      });
    } catch (error) {
      message.error(`Failed To Load Asset Allocation Items - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  redirectToViewAssetAllocationItemPage(): void {
    this.props.history.push(`/asset-allocation-items/${this.state.id}`);
  }

  async createAssetAllocationItem(): Promise<void> {
    interface Response {
      assetAllocationItem: AssetAllocationItem;
      errors: APIError[];
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_ASSET_ALLOCATION_ITEM,
        variables: data,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createAssetAllocationItem",
      );

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.setState({ id: mutation?.assetAllocationItem?.id }, () => {
          message.success("Asset allocation item created sucessfully");
          this.redirectToViewAssetAllocationItemPage();
        });
      }
    } catch (error) {
      message.error(`Failed to create asset allocation item - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleCreateAssetAllocationItemModal();
    }
  }

  render(): JSX.Element | null {
    const { isLoading, assetAllocationItems, page, pageSize, showNewAssetAllocationItemModal } = this.state;

    const resultsWithKeys: AssetAllocationItem[] = assetAllocationItems.map(
      (result: AssetAllocationItem): AssetAllocationItem => {
        return {
          ...result,
          id: result.id,
        };
      },
    );

    return (
      <Card>
        <div className="categories-container">
          <PageToolbar
            totalCount={assetAllocationItems.length}
            currentDataCount={assetAllocationItems.length}
            page={page}
            pageSize={pageSize}
            modelName="asset allocation items"
            primaryAction={
              <EntitledSection
                entitlement={entitlements.EconomicIndicators.create}
              >
                <Button
                  className="base-button"
                  size="large"
                  data-testid="create-category-button"
                  onClick={this.toggleCreateAssetAllocationItemModal}
                >
                  <PlusOutlined />
                  Create Asset Allocation Item
                </Button>
              </EntitledSection>
            }
          />

          <Row>
            <Col span={24}>
              <Table
                loading={isLoading}
                scroll={{ x: 400 }}
                columns={getAssetAllocationItemsColumn(
                  assetAllocationItems,
                )}
                onRow={(record) => ({
                  onClick: () => {
                    this.props.history.push({
                      pathname: `/asset-allocation-items/${record.id}`,
                      state: { assetAllocationItems },
                    });
                  },
                  style: { cursor: 'pointer' }

                })}
                dataSource={resultsWithKeys}
                pagination={{
                  current: page,
                  defaultPageSize: 25,
                  total: assetAllocationItems.length,
                  onChange: this.nextPage,
                  showTotal: showTotalPagination,
                }}
              />
            </Col>
          </Row>

          {showNewAssetAllocationItemModal &&
            ModalCreator({
              title: "Create Asset Allocation Item",
              okType: "primary",
              okText: "Create",
              showModal: showNewAssetAllocationItemModal,
              onOkay: this.createAssetAllocationItem,
              toggleModal: this.toggleCreateAssetAllocationItemModal,
              inscription: NewAssetAllocationItemUI({
                reference: this.reference,
              }),
            })}
        </div>
      </Card>
    );
  }
}
